/* eslint-disable node/prefer-global/process */
import type { InstallXOptions, SnippetConfig } from '@empathyco/x-components'
import { cssInjector, I18n } from '@empathyco/x-archetype-utils'
import { filter } from '@empathyco/x-components'
import { addQueryToHistoryQueries } from '@empathyco/x-components/history-queries'
import { trackNoResultsQueryWithFallbackWireDebounced } from '@empathyco/x-components/tagging'
import { setUrlQuery } from '@empathyco/x-components/url'
import { adapter } from '../adapter/adapter'
import AppComponent from '../App.vue'
import { useDevice } from '../composables/use-device.composable'
import * as messages from '../i18n/messages'
import store from '../store'
import { searchBoxOptions, searchBoxPrivateOptions } from './search-box/search-box.options'
import { searchOptions, searchPrivateOptions } from './search/search.options'
import { mergeSemanticQueriesConfigWire } from './wiring/semantic-queries.wiring'

const device = useDevice()

const addQueryToHistoryQueriesFiltered = filter(
  addQueryToHistoryQueries,
  ({ eventPayload }) => !eventPayload.startsWith('::'),
)

const setUrlQueryFiltered = filter(
  setUrlQuery,
  ({ eventPayload }) => !eventPayload.startsWith('::'),
)

/**
 * Function that returns the options to install x-components.
 *
 * Returns - the InstallXOptions.
 */
export async function getInstallXOptions(): Promise<InstallXOptions> {
  return {
    adapter,
    store,
    rootComponent: AppComponent,
    domElement: getDomElement,
    xModules: {
      searchBox: searchBoxOptions,
      facets: {
        config: {
          filtersStrategyForRequest: 'leaves-only',
        },
      },
      semanticQueries: {
        config: {
          threshold: 50,
          maxItemsToRequest: 10,
        },
        wiring: {
          SemanticQueriesConfigProvided: {
            mergeSemanticQueriesConfigWire,
          },
        },
      },
      search: searchOptions,
      historyQueries: {
        wiring: {
          UserAcceptedAQuery: {
            addQueryToHistoryQueries: addQueryToHistoryQueriesFiltered,
          },
        },
      },
      url: {
        wiring: {
          UserAcceptedAQuery: {
            setUrlQuery: setUrlQueryFiltered,
          },
        },
      },
      tagging: {
        wiring: {
          FallbackNoResultsResponseChanged: {
            trackNoResultsQueryWithFallbackWireDebounced,
          },
          RelatedPromptsResponseChanged: {
            // @ts-expect-error: Override the default wire
            trackNoResultsQueryWithFallbackWireDebounced: undefined,
          },
          SemanticQueriesResponseChanged: {
            // @ts-expect-error: Override the default wire
            trackNoResultsQueryWithFallbackWireDebounced: undefined,
          },
        },
      },
    },
    __PRIVATE__xModules: {
      search: searchPrivateOptions,
      searchBox: searchBoxPrivateOptions,
    },
    async installExtraPlugins({ app, snippet }) {
      const i18n = await I18n.create({
        locale: snippet.uiLang,
        device: (snippet.device as string) ?? device.deviceName.value,
        fallbackLocale: 'en',
        messages,
      })

      app.use(i18n)
      app.config.globalProperties.$setLocale = i18n.setLocale.bind(i18n)
      app.config.globalProperties.$setLocaleDevice = i18n.setDevice.bind(i18n)
    },
  }
}

/**
 * Creates a DOM element to mount the X Components app.
 *
 * @param snippetConfig - The snippet configuration.
 * @param snippetConfig.isolate - Whether to isolate the DOM element using Shadow DOM.
 * @returns The DOM element.
 */
function getDomElement({ isolate }: SnippetConfig): Element {
  const container = document.createElement('div')
  container.classList.add('x-root-container')
  const domElement = document.createElement('div')

  if (isolate || process.env.NODE_ENV === 'production') {
    const shadowRoot = container.attachShadow({ mode: 'open' })
    shadowRoot.appendChild(domElement)
    cssInjector.setHost(shadowRoot)
  } else {
    container.appendChild(domElement)
    cssInjector.setHost(document.head)
  }

  document.body.appendChild(container)
  return domElement
}
